import { TenantService } from '@lib/services/tenant.service';
import { Permission, TenantFlow } from '../enums';
import { MenuItem } from '../interfaces';
import { TenantConfigurationService } from '@lib/services';
import { inject } from '@angular/core';
import { customerTenant } from '@env/environment';
import { Utils } from '@lib/utils';

/**
 * Checks if the tenant has access to a specific flow and returns the provided permissions
 * if allowed, or an empty array if not allowed.
 *
 * @param permissions - An array of permission strings to return if access is allowed.
 * @returns An array of permissions if the tenant is allowed, otherwise an empty array.
 */
function isTenantAccessAllowed(permissions: string[], flow: TenantFlow) {
    const tenantService = inject(TenantService);
    const tenantKey = tenantService.getTenantKey();

    const tenantConfigService = new TenantConfigurationService();
    return tenantConfigService.isTenantAllowed(tenantKey || '', flow) ? permissions : [''];
}

export const getMenuItems: () => MenuItem[] = () => [
    {
        title: 'Home',
        icon: 'home',
        link: '/home',
        // PS-9334: hidden for all tenants — dashboard data is incomplete/incorrect pending BE integration.
        // Restore to [Permission.Home] once the feature is fully integrated and verified.
        permissions: [''],
    },
    {
        title: 'Analytics',
        icon: 'report',
        link: '/report',
        permissions: [Permission.ProductManagement, Permission.CardProducts],
    },
    {
        title: 'Customers',
        icon: 'customer',
        link: '/customers',
        permissions: [Permission.Customer],
    },
    {
        title: 'Agents',
        icon: 'customer',
        link: '/agents',
        permissions: isTenantAccessAllowed([Permission.AgentView], TenantFlow.AGENT_SEPARATE_MODULE),
    },
    {
        title: 'Card Product',
        icon: 'product',
        link: '/card-product',
        permissions: [Permission.ProductManagement, Permission.CardProducts],
    },
    {
        title: 'Access Management',
        icon: 'access-management',
        link: '/access-management',
        permissions: [Permission.AccessManagement],
    },
    {
        title: 'Atm',
        icon: 'atm-icon',
        link: '/atm',
        permissions: [Permission.Locations],
    },
    {
        title: 'Exchange Rate',
        icon: 'customer-operations',
        link: '/exchange-rate-managment',
        permissions: isTenantAccessAllowed([Permission.FxRates], TenantFlow.EXCHANGE_RATE_ALLOWED),
    },
    {
        title: 'Currency Controls',
        icon: 'currency-controls',
        link: '/currency-controls',
        permissions: isTenantAccessAllowed([Permission.GetCurrencyControlsList], TenantFlow.CURRENCY_CONTROLS_MODULE),
    },
    {
        title: 'Customer Cash-In/Out',
        icon: 'customer-operations',
        link: '/customer-operations',
        permissions: [Permission.CardLessVouchers],
    },
    {
        title: 'Transactions',
        icon: 'transaction',
        link: '/transactions',
        permissions: [Permission.Transactions],
    },
    {
        title: 'Cards',
        icon: 'card-icon-sidemenu',
        link: '/cards',
        permissions: [Permission.Cards],
    },
    {
        title: 'Card Request',
        icon: 'card-icon-sidemenu',
        link: '/card-request',
        permissions: isTenantAccessAllowed([Permission.CustomerCardRequest], TenantFlow.CARD_REQUEST_ACCESS_ALLOWED),
    },
    {
        title: 'Activity Logger',
        icon: 'activity-logger',
        link: '/activitylog',
        permissions: isTenantAccessAllowed([Permission.ActivityLogger], TenantFlow.ACTIVITY_LOGGER_MODULE),
    },
    {
        title: 'Pending Requests',
        icon: 'pending-requests',
        link: '/pending-requests',
        permissions: isTenantAccessAllowed(
            [
                Permission.MakerCheckerWorkflow,
                Permission.Cards,
                Permission.CardRequestsMaker,
                Permission.CardRequestsChecker,
                Permission.CustomerAccountLoadFunds,
                Permission.CheckerLoadFunds,
                Permission.MakerCheckerLoadFundsElevated,
                Permission.CustomerAccountUnloadFunds,
                Permission.CheckerUnloadFunds,
                Permission.MakerCheckerUnloadFundsElevated,
                Permission.GetPendingRequestsForAgents,
            ],
            TenantFlow.PENDING_REQUESTS_MODULE,
        ),
    },
    {
        title: 'Product Settings',
        icon: 'settings',
        link: '/product-settings',
        permissions: [Permission.ProductSettings],
    },
    {
        title: 'Bulk Upload Cash',
        icon: 'upload',
        link: '/bulk-upload-cash',
        permissions: [Permission.BulkUpload],
    },
    {
        title: 'Bulk Card Issuance',
        icon: 'upload',
        link: '/bulk-card-issuance',
        permissions: isTenantAccessAllowed([Permission.BulkCardIssuanceCreation], TenantFlow.BULK_CARD_ISSUANCE_MODULE),
    },
    {
        title: 'Call Center',
        icon: 'customer',
        link: '/call-center',
        permissions: [Permission.Customer],
    },
    // {
    //     title: 'Faq',
    //     icon: 'faq-icon',
    //     link: '/faq',
    //     permissions: [Permission.Faq],
    // },
];

export const getlowerMenuItems: () => MenuItem[] = () => {
    const utils = inject(Utils);
    const menuItems: MenuItem[] = [
        {
            section: 'PERSONAL',
            title: 'Feedback',
            icon: 'feedback',
            permissions: [],
            link: '/feedback',
        },
        {
            section: 'MANUALS',
            title: 'API Catalog',
            icon: 'api-catalog',
            permissions: [],
            external: true,
            link: 'https://nymcard.com/docs/api',
        },
        {
            title: 'Getting Started Guide',
            icon: 'guide',
            permissions: [],
            external: true,
            link: 'https://docs.nymcard.com/',
        },
    ];
    const tenantService = inject(TenantService);
    const tenantKey = tenantService.getTenantKey();

    //Hiding the menuItem **Settings** if all toggle items are false
    const isExchangeRateTenant = [customerTenant.INC, customerTenant.NASS].includes(tenantKey?.split('_')[0] ?? '');
    if ((isExchangeRateTenant && utils.getPermissions(Permission.SearchFxRates)) || utils.getPermissions(Permission.Logo)) {
        menuItems.push({
            title: 'Settings',
            icon: 'settings',
            permissions: [Permission.Settings],
            link: '/control-center/settings',
        });
    }

    return menuItems;
};
